import type {
  BacklogChildRow,
  EvidenceItem,
  LiffFinancialWorkCard,
} from "./types";
import { buildReceivableSlipImageUrl } from "@/lib/pendingOrders";

function incomeRecordImageUrl(recordId: string, workspaceId: string): string {
  return `/api/v1/transactions/${encodeURIComponent(recordId)}/image?workspace_id=${encodeURIComponent(workspaceId)}`;
}

function transactionRecordImageUrl(
  recordId: string,
  workspaceId: string,
): string {
  return incomeRecordImageUrl(recordId, workspaceId);
}

function resolveEvidenceImageUrl(
  item: EvidenceItem,
  workspaceId: string,
): string {
  const direct = item.imageUrl?.trim() || "";
  if (direct) return direct;
  const workerId = item.dataNav?.workerId;
  if (workerId !== "bill-lin-income" && workerId !== "bill-lin-expense") {
    return "";
  }
  const recordId = item.dataNav?.recordId?.trim() || "";
  if (!recordId || !workspaceId) return "";
  return transactionRecordImageUrl(recordId, workspaceId);
}

/** Same-origin image for an evidence row — empty when none exists. */
export function resolveEvidenceItemImageUrl(
  item: EvidenceItem,
  workspaceId: string,
): string {
  return resolveEvidenceImageUrl(item, workspaceId.trim());
}

function resolveBacklogChildImageUrl(
  row: BacklogChildRow,
  workspaceId: string,
): string {
  const direct = row.slipImageUrl?.trim() || "";
  if (direct) return direct;
  const workerId = row.dataNav?.workerId;
  if (workerId !== "bill-lin-income" && workerId !== "bill-lin-expense") {
    return "";
  }
  const recordId = row.dataNav?.recordId?.trim() || "";
  if (!recordId || !workspaceId) return "";
  return transactionRecordImageUrl(recordId, workspaceId);
}

/** Same-origin slip/income image for a queue row — empty when none exists. */
export function resolveQueueCardImageUrl(
  card: LiffFinancialWorkCard,
  workspaceId: string,
  ownerUserId?: string | null,
): string {
  const ws = workspaceId.trim();

  const fromContext = card.contextRows
    ?.find((row) => row.key === "slip_image" || row.key === "image_url")
    ?.value?.trim();
  if (fromContext) return fromContext;

  if (!ws && !ownerUserId?.trim()) return "";

  for (const item of card.evidenceItems ?? []) {
    const url = resolveEvidenceImageUrl(item, ws);
    if (url) return url;
  }

  const selfChild = (card.backlogChildren ?? []).find(
    (row) =>
      row.objectRef.kind === card.objectRef.kind &&
      row.objectRef.id === card.objectRef.id,
  );
  if (selfChild) {
    const url = resolveBacklogChildImageUrl(selfChild, ws);
    if (url) return url;
  }

  for (const row of card.backlogChildren ?? []) {
    if (
      row.slipImageUrl?.trim() ||
      row.dataNav?.workerId === "bill-lin-income" ||
      row.dataNav?.workerId === "bill-lin-expense"
    ) {
      const url = resolveBacklogChildImageUrl(row, ws);
      if (url) return url;
    }
  }

  const closed =
    card.workPlacement?.bucket === "recently_closed" ||
    card.surfaceState === "closed";
  // Closed rows only show a photo when the feed stamped slip_image — do not
  // invent a transactions/image URL from open_data (payment id ≠ record_id, and
  // bank-only income has no slip).
  if (closed) {
    const imageId = card.contextRows
      ?.find((row) => row.key === "image_id")
      ?.value?.trim();
    if (imageId && ownerUserId?.trim()) {
      return buildReceivableSlipImageUrl(imageId, ownerUserId) ?? "";
    }
    return "";
  }

  const nav = card.availableActions.find(
    (action) => action.actionType === "open_data",
  )?.dataNav;
  if (
    nav?.workerId === "bill-lin-income" ||
    nav?.workerId === "bill-lin-expense"
  ) {
    const recordId = nav.recordId?.trim() || "";
    if (recordId && ws) return transactionRecordImageUrl(recordId, ws);
  }

  const imageId = card.contextRows
    ?.find((row) => row.key === "image_id")
    ?.value?.trim();
  if (imageId && ownerUserId?.trim()) {
    return buildReceivableSlipImageUrl(imageId, ownerUserId) ?? "";
  }

  return "";
}
